import classNames from 'classnames';
import type { PointerEvent} from 'react';
import { useMemo } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import useUser from '../../login/userContext';
import type { Product } from '../../model/types';
import { preconnect } from '../../utils/HeaderContext';
import useBooleanState from '../../utils/useBooleanState';
import { productImageToSrcSet } from '../ProductImage';
import * as style from './ProductDetailsImage.module.css';

export interface Props {
    product: Product;
}

export default function ProductDetailsImage({ product }: Props) {
    const [showZoom, setShowZoom] = useBooleanState();
    const [scaledUp, setScaledUp] = useBooleanState();

    const zoomImageUrl = useProductImage(product, 'superZoom');
    const defaultImageUrl = useProductImage(product, 'product');
    const altText = `Bilde av produktet ${product.name}`;
    const srsSet = useMemo(() => productImageToSrcSet(defaultImageUrl, [150, 300, 600, 900, 1200, 1500, 1800]), [defaultImageUrl]);

    const canZoom = useMediaQuery('(width >= 768px)'); // Only show zoom on desktop

    // Preconnect to the subdomain that contains the product images
    preconnect('https://bilder.vinmonopolet.no');

    return (
        <div className={style.container}>
            <img
                className={classNames({ [style.expired]: product.expired, [style.scaledUp]: scaledUp && !canZoom })}
                srcSet={srsSet}
                sizes={scaledUp && !canZoom ? '100vw' : '(min-width: 1440px) 380px, 300px'}
                alt={altText}
                onPointerEnter={canZoom ? setShowZoom.toTrue : undefined}
                onPointerLeave={canZoom ? setShowZoom.toFalse : undefined}
                onPointerMove={reposition}
                onClick={canZoom ? undefined : setScaledUp.toggle}
            />
            {showZoom && <img className={style.zoom} src={zoomImageUrl} alt={altText} loading="lazy" />}

            <Expired product={product} />
            <PressImageLink product={product} />
        </div>
    );
}

function Expired({ product }: { product: Product }) {
    return product.expired ? <div className={style.expiredImg}>Utgått</div> : null;
}

function PressImageLink({ product }: { product: Product }) {
    const { user } = useUser();

    if (!user?.pressUser) return null;

    return (
        <div>
            <a className="no-print" href={`https://bilder.vinmonopolet.no/cache/5000x5000-0/${product.code}-1.jpg`} target="_blank" rel="noreferrer">
                Last ned bilde i høy oppløsning
            </a>
        </div>
    );
}

function reposition(e: PointerEvent<HTMLImageElement>) {
    const img = e.currentTarget;
    const lensImg = img.nextElementSibling as HTMLImageElement | undefined;
    if (!lensImg) return;
    const { x, y, width, height } = img.getBoundingClientRect();

    const posX = e.clientX - x;
    const posY = e.clientY - y;

    const halfWidth = width / 2;
    const halfHeight = height / 2;

    const tx = halfWidth - (posX / img.offsetWidth) * lensImg.naturalWidth;
    const ty = halfHeight - (posY / img.offsetHeight) * lensImg.naturalHeight;

    lensImg.style.objectPosition = `${tx}px ${ty}px`;
}

function useProductImage(product: Product, type: string) {
    return useMemo(() => product.images?.find((image) => image.format === type)?.url ?? '', [product.images]);
}
